import { useState, useMemo } from 'react'
import { AppLayout } from '@/components/layout/AppLayout'
import { AtlasPage } from '@/components/ui/AtlasPage'
import { PageTitle } from '@/components/ui/PageTitle'
import { Separator } from '@/components/ui/Separator'
import { PageNumber } from '@/components/ui/PageNumber'
import { MonthNavigation } from '@/components/calendar/MonthNavigation'
import { OverlayToggles } from '@/components/calendar/OverlayToggles'
import type { Overlays } from '@/components/calendar/OverlayToggles'
import { PlantFilter } from '@/components/calendar/PlantFilter'
import { CalendarGrid } from '@/components/calendar/CalendarGrid'
import { DaySidebar } from '@/components/calendar/DaySidebar'
import type { CalendarTask, CalendarLog, CalendarPlant } from '@/types/calendar'
import type { WeatherDaily } from '@/types/weather'

interface Props {
  month: string
  tasks: CalendarTask[]
  logs: CalendarLog[]
  plants: CalendarPlant[]
  weather_daily: WeatherDaily[] | null
}

function CalendarIndex({ month, tasks, logs, plants, weather_daily }: Props) {
  const [selectedDate, setSelectedDate] = useState<string | null>(() => {
    const stored = sessionStorage.getItem('calendar_selected_date')
    if (stored) {
      sessionStorage.removeItem('calendar_selected_date')
      return stored
    }
    return null
  })
  const [overlays, setOverlays] = useState<Overlays>({ tasks: true, completed: true, weather: true })
  const [selectedPlantIds, setSelectedPlantIds] = useState<number[]>([])


  const toggleOverlay = (key: keyof Overlays) => {
    setOverlays(prev => ({ ...prev, [key]: !prev[key] }))
  }

  // Filter data by selected plants
  const filteredTasks = useMemo(() => {
    if (selectedPlantIds.length === 0) return tasks
    return tasks.filter(t => selectedPlantIds.includes(t.plant_id))
  }, [tasks, selectedPlantIds])

  const filteredLogs = useMemo(() => {
    if (selectedPlantIds.length === 0) return logs
    return logs.filter(l => selectedPlantIds.includes(l.plant_id))
  }, [logs, selectedPlantIds])

  // Data for sidebar
  const sidebarData = useMemo(() => {
    if (!selectedDate) return null
    const dateKey = selectedDate
    const dayTasks = filteredTasks.filter(t => t.next_due_at?.slice(0, 10) === dateKey)
    const dayLogs = filteredLogs.filter(l => l.performed_at?.slice(0, 10) === dateKey)
    const dayWeather = weather_daily?.find(w => w.date === dateKey)
    return { tasks: dayTasks, logs: dayLogs, weather: dayWeather }
  }, [selectedDate, filteredTasks, filteredLogs, weather_daily])

  const monthLabel = new Date(month + '-01').toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })

  return (
    <>
      <AtlasPage>
        <PageTitle title="Kalendarz" subtitle={monthLabel} />
        <Separator />

        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-sm mb-md">
          <OverlayToggles overlays={overlays} onToggle={toggleOverlay} />
          <div className="flex items-center gap-sm">
            <PlantFilter plants={plants} selectedIds={selectedPlantIds} onChange={setSelectedPlantIds} />
            <MonthNavigation month={month} />
          </div>
        </div>

        <CalendarGrid
          month={month}
          tasks={filteredTasks}
          logs={filteredLogs}
          weatherDaily={weather_daily}
          overlays={overlays}
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
        />

        <PageNumber number={8} />
      </AtlasPage>

      {selectedDate && sidebarData && (
        <DaySidebar
          date={selectedDate}
          tasks={sidebarData.tasks}
          logs={sidebarData.logs}
          weather={sidebarData.weather}
          overlays={overlays}
          onClose={() => setSelectedDate(null)}
        />
      )}
    </>
  )
}

CalendarIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
export default CalendarIndex
